/**
 * ContainerCommentsDialog — Excel-style threaded comments for a shipment.
 *
 * Opens on right-click (desktop) or long-press (mobile) of a container number.
 * Lists all comments newest-first, lets the user add new ones, edit/delete
 * their own (or any if admin/super-admin), and live-refreshes via WebSocket
 * events broadcast by the backend (`comment_added` / `comment_updated` /
 * `comment_deleted`).
 *
 * Props
 *   open / onOpenChange  — dialog visibility (controlled)
 *   shipment             — the shipment object (id, container_number)
 *   api                  — axios instance from useApi()
 *   currentUser          — { user_id, role, ... } from AuthContext
 *   onCountChange(n)     — fired after every mutation so the dashboard can
 *                          update its per-row indicator dot
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Loader2, MessageSquare, Send, Trash2, Pencil, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import { ShipmentFeesSection } from '@/components/ShipmentFeesSection';

const formatStamp = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    if (sameDay) {
      return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    }
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const CommentItem = ({ comment, canMutate, onEdit, onDelete, savingEditId }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.text);

  const cancel = () => { setDraft(comment.text); setEditing(false); };
  const save = async () => {
    if (!draft.trim() || draft.trim() === comment.text) { cancel(); return; }
    await onEdit(comment.id, draft.trim());
    setEditing(false);
  };

  return (
    <div className="bg-slate-800/60 border border-slate-800 rounded-lg p-3" data-testid={`comment-${comment.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium text-white truncate">{comment.user_name}</span>
          {comment.user_role && (
            <Badge variant="outline" className="text-[10px] border-slate-600 text-slate-400 px-1.5 py-0">
              {comment.user_role.replace(/_/g, ' ')}
            </Badge>
          )}
        </div>
        <span className="text-[11px] text-slate-500 whitespace-nowrap" title={comment.created_at}>
          {formatStamp(comment.created_at)}
          {comment.edited && ' · edited'}
        </span>
      </div>

      {editing ? (
        <div className="mt-2 space-y-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            maxLength={2000}
            className="bg-slate-900 border-slate-700 text-sm text-white"
            data-testid={`comment-edit-textarea-${comment.id}`}
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={cancel} className="h-7 border-slate-700">
              <X className="h-3.5 w-3.5 mr-1" /> Cancel
            </Button>
            <Button size="sm" onClick={save} disabled={savingEditId === comment.id} className="h-7 bg-blue-600 hover:bg-blue-500">
              {savingEditId === comment.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />}
              Save
            </Button>
          </div>
        </div>
      ) : (
        <>
          <p className="mt-1.5 text-sm text-slate-200 whitespace-pre-wrap">{comment.text}</p>
          {canMutate && (
            <div className="mt-1.5 flex gap-3">
              <button
                onClick={() => setEditing(true)}
                className="text-[11px] text-slate-500 hover:text-blue-300 inline-flex items-center gap-1"
                data-testid={`comment-edit-btn-${comment.id}`}
              >
                <Pencil className="h-3 w-3" /> Edit
              </button>
              <button
                onClick={() => onDelete(comment.id)}
                className="text-[11px] text-slate-500 hover:text-red-300 inline-flex items-center gap-1"
                data-testid={`comment-delete-btn-${comment.id}`}
              >
                <Trash2 className="h-3 w-3" /> Delete
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const ContainerCommentsDialog = ({
  open,
  onOpenChange,
  shipment,
  api,
  currentUser,
  onCountChange,
}) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savingEditId, setSavingEditId] = useState(null);
  const [draft, setDraft] = useState('');
  const listEndRef = useRef(null);

  const shipmentId = shipment?.id;

  // Initial fetch + refetch whenever the dialog opens with a different shipment.
  useEffect(() => {
    if (!open || !shipmentId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/shipments/${shipmentId}/comments`)
      .then(({ data }) => {
        if (cancelled) return;
        setComments(Array.isArray(data) ? data : []);
        onCountChange?.(Array.isArray(data) ? data.length : 0);
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err?.response?.data?.detail || 'Could not load comments');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, shipmentId, api, onCountChange]);

  // Live updates via the global window event the dashboard re-broadcasts from
  // its websocket handler. (Keeping the dialog decoupled from the socket so
  // we don't open a second connection.)
  useEffect(() => {
    if (!open || !shipmentId) return;
    const handler = (e) => {
      const { type, shipment_id, comment, comment_id } = e.detail || {};
      if (shipment_id !== shipmentId) return;
      if (type === 'comment_added' && comment) {
        setComments((prev) => prev.some((c) => c.id === comment.id) ? prev : [comment, ...prev]);
        onCountChange?.((prev) => (prev || 0) + 1);
      } else if (type === 'comment_updated' && comment) {
        setComments((prev) => prev.map((c) => (c.id === comment.id ? comment : c)));
      } else if (type === 'comment_deleted' && comment_id) {
        setComments((prev) => prev.filter((c) => c.id !== comment_id));
        onCountChange?.((prev) => Math.max(0, (prev || 0) - 1));
      }
    };
    window.addEventListener('shipment-comment-event', handler);
    return () => window.removeEventListener('shipment-comment-event', handler);
  }, [open, shipmentId, onCountChange]);

  // Auto-scroll to the bottom when new comments arrive
  useEffect(() => {
    if (open && listEndRef.current) {
      // Newest at top, so we scroll to top — but if user is editing keep position.
    }
  }, [comments, open]);

  const submit = useCallback(async () => {
    const text = draft.trim();
    if (!text) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/shipments/${shipmentId}/comments`, { text });
      // Optimistic merge (server broadcasts, but we may receive our own echo)
      setComments((prev) => prev.some((c) => c.id === data.id) ? prev : [data, ...prev]);
      setDraft('');
      onCountChange?.((prev) => (prev || 0) + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  }, [draft, api, shipmentId, onCountChange]);

  const handleEdit = useCallback(async (id, text) => {
    setSavingEditId(id);
    try {
      const { data } = await api.patch(`/comments/${id}`, { text });
      setComments((prev) => prev.map((c) => (c.id === id ? data : c)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to edit comment');
    } finally {
      setSavingEditId(null);
    }
  }, [api]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await api.delete(`/comments/${id}`);
      setComments((prev) => prev.filter((c) => c.id !== id));
      onCountChange?.((prev) => Math.max(0, (prev || 0) - 1));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete comment');
    }
  }, [api, onCountChange]);

  const canMutate = useCallback((c) => {
    const role = currentUser?.role;
    return c.user_id === currentUser?.user_id || ['super_admin', 'admin'].includes(role);
  }, [currentUser]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-lg max-h-[80vh] flex flex-col" data-testid="container-comments-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-blue-400" />
            Comments
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {shipment?.container_number || 'Container'} &middot; visible to your company
          </DialogDescription>
        </DialogHeader>

        {/* Thread */}
        <div className="flex-1 overflow-y-auto -mr-2 pr-2 space-y-2 min-h-[80px]">
          {loading && (
            <div className="flex items-center justify-center py-6 text-slate-500 text-sm">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
            </div>
          )}
          {!loading && comments.length === 0 && (
            <p className="text-center text-sm text-slate-500 py-6">
              No comments yet. Be the first to add one.
            </p>
          )}
          {!loading && comments.map((c) => (
            <CommentItem
              key={c.id}
              comment={c}
              canMutate={canMutate(c)}
              onEdit={handleEdit}
              onDelete={handleDelete}
              savingEditId={savingEditId}
            />
          ))}
          <div ref={listEndRef} />
        </div>

        {/* Composer */}
        <div className="border-t border-slate-800 pt-3 space-y-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add a comment for your team…"
            maxLength={2000}
            rows={2}
            className="bg-slate-800 border-slate-700 text-sm text-white resize-none"
            data-testid="comment-new-textarea"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                submit();
              }
            }}
          />
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-slate-500">⌘/Ctrl + Enter to send · {draft.length}/2000</span>
            <Button
              size="sm"
              onClick={submit}
              disabled={submitting || !draft.trim()}
              className="bg-blue-600 hover:bg-blue-500"
              data-testid="comment-submit-btn"
            >
              {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}
              Comment
            </Button>
          </div>
        </div>

        {/* iter 264d — Billable fees checklist. Sits right below comments so
            dispatchers can flag chargeable fees while they're already
            documenting shipment context. Accounting picks these up via
            GET /api/accounting/pending-fees. */}
        {shipment?.id && (
          <div className="border-t border-slate-800 pt-3">
            <ShipmentFeesSection shipmentId={shipment.id} compact />
          </div>
        )}

        <DialogFooter className="border-t border-slate-800 pt-2 sm:justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-700" data-testid="comment-close-btn">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
